import React, { useState } from "react";
import {
  Row,
  Col,
  OverlayTrigger,
  Tooltip as BootstrapTooltip,
} from "react-bootstrap";
import {
  ComposedChart,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Bar,
  Line,
  ReferenceArea,
  ResponsiveContainer,
  TooltipPayload,
} from "recharts";
import { FullToggle } from "../../../components/FullToggle";
import TooltipHelper from "../../../components/TooltipHelper";

interface PositiveRateProps {
  data: App.DailyData[];
  dataKeyFormalNames: [string, string];
  currentFilter: App.CurrentFilter;
  reportDate: string;
  dataKeys: [
    [keyof App.DailyElement, keyof App.DailyElement],
    [keyof App.DailyElement, keyof App.DailyElement]
  ];
  color: string;
  darkColor: string;
  startPre?: string;
  percent?: boolean;
}

export default function Rate(props: PositiveRateProps) {
  const {
    data,
    color,
    darkColor,
    dataKeyFormalNames,
    dataKeys,
    startPre,
    percent,
    currentFilter,
  } = props;

  const [individualToggle, setIndividualToggle] = useState(false);

  function referenceLabel(props: any) {
    return (
      <text
        className="prelim-text"
        x={props.viewBox.x - 65}
        y={props.viewBox.y + 30}
      >
        prelim &rarr;
      </text>
    );
  }

  function CustomTooltip({
    payload,
    label,
    active,
  }: {
    payload?: ReadonlyArray<TooltipPayload> | undefined;
    label?: string | number | undefined;
    active?: boolean | undefined;
  }) {
    if (active && payload && payload.length > 1) {
      const rate = payload[0].value as number;
      const rateMoving = payload[1].value as number;
      const testConducted = data.find((record) => record.date === label)
        ?.existingCases;
      return (
        <div className="chart-tooltip bg-white">
          <h5 className="font-weight-normal mb-1">Date: {label}</h5>
          <p>
            {testConducted === 0 && rate === 0 ? (
              <React.Fragment>
                <i className="fas fa-exclamation-circle mr-2"></i>
                <span>No Specimen Collected</span>
              </React.Fragment>
            ) : null}
          </p>
          <p className="mb-1">
            <span style={{ color: color }}>{"\u25A0"} </span>
            {dataKeyFormalNames[0]}: {rate} {percent ? "%" : ""}
          </p>
          <p className="mb-0">
            <span style={{ color: darkColor }}>{"\u25A0"} </span>
            {dataKeyFormalNames[1]}: {rateMoving} {percent ? "%" : ""}
          </p>
        </div>
      );
    } else if (active && payload && payload.length === 1) {
      const rate = payload[0].value as number;
      const testConducted = data.find((record) => record.date === label)
        ?.existingCases;
      return (
        <div className="chart-tooltip bg-white">
          <h5 className="font-weight-normal mb-1">Date: {label}</h5>
          <p>
            {testConducted === 0 ? (
              <React.Fragment>
                <i className="fas fa-exclamation-circle mr-2"></i>
                <span>No Specimen Collected</span>
              </React.Fragment>
            ) : null}
          </p>
          <p className="mb-1">
            <span style={{ color: color }}>{"\u25A0"} </span>
            {dataKeyFormalNames[0]}: {rate} {percent ? "%" : ""}
          </p>
        </div>
      );
    }

    return null;
  }

  return (
    <React.Fragment>
      <Row>
        <Col xs={12} md={6}>
          <div className="d-flex">
            <h5 className="mb-0">{`${
              !currentFilter.filters.length
                ? "Statewide"
                : currentFilter.displayName
            } Daily Positive Test Rate`}</h5>
            <TooltipHelper
              text={`The positivity rate calculation uses the specimen collection date for both the positive cases and tested individuals in order to accurately capture how many tests administered on a given day produced a positive result. 
              However, the positive case count reported on the dashboard is based on the date the positive test was reported to and confirmed by the IDOH system. As a result, calculations of the positive rate using cases reported per day and 
              number of tests performed per day will not equal the positivity rate. Positivity rate may change as additional test results—both positive and negative—are submitted to IDOH over time. More information on the rate calculation methodology 
              is provided under Additional Resources at the top of the dashboard`}
            />
          </div>
          <OverlayTrigger
            key="bottom"
            placement="bottom"
            overlay={
              <BootstrapTooltip id="tooltip-bottom">
                Receiving enough testing data to perform rate calculations
                typically takes 6 days. Therefore, the data within the
                preliminary shaded area are provisional and should not be
                characterized as comprehensive and complete data.
              </BootstrapTooltip>
            }
          >
            <span
              className="mt-1 cursor-pointer"
              style={{
                textDecoration: "underline dotted",
                fontSize: ".8rem",
              }}
            >
              {" "}
              What does prelim mean?{" "}
            </span>
          </OverlayTrigger>
        </Col>
        <Col className="d-flex justify-content-end" xs={12} md={6}>
          <div className="text-right">
            <FullToggle
              options={["All Tests", "Individuals"]}
              checked={individualToggle}
              setChecked={setIndividualToggle}
            />
          </div>
        </Col>
      </Row>
      <ResponsiveContainer height={300}>
        <ComposedChart data={data}>
          <XAxis dataKey="date" />
          <YAxis tickFormatter={(value) => `${value} ${percent ? "%" : ""}`} />
          <Tooltip content={CustomTooltip} />
          <Legend />
          <Bar
            name={dataKeyFormalNames[0]}
            dataKey={dataKeys[individualToggle ? 0 : 1][0]}
            barSize={20}
            fill={color}
          />
          {startPre ? (
            <ReferenceArea
              x1={startPre}
              stroke={color}
              strokeOpacity={0.3}
              label={referenceLabel}
            />
          ) : null}
          <Line
            dot={false}
            legendType="plainline"
            strokeWidth="2"
            name={dataKeyFormalNames[1]}
            type="monotone"
            dataKey={dataKeys[individualToggle ? 0 : 1][1]}
            stroke={darkColor}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </React.Fragment>
  );
}
